import React, { useState, useEffect } from "react";

import Modal from "react-modal";
import axios from "axios";

import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";

import { ProductSearchHelper } from "../../helpers";
import { Separator } from "../../components";
import { Button, ButtonGroup, Icon, Tabs } from "../../components/ui";
import { FilterBullets, FilterList, FilterOptions, SearchCount } from "./index.jsx";

import { filterModalStyles } from "../../core/utils/responsiveModalStyles";

const modalStyles = filterModalStyles;

try {
  Modal.setAppElement("#app");
} catch (e) {
  // ignore (for tests)
}

const SearchFilters = ({
  active,
  close,
  search: defaultSearch,
  onApply,
  includeStatusFilter = false,
}) => {
  const showModal = window.innerWidth >= 1200;

  const initialSearch = {
    ...defaultSearch,
    filters: {
      ...defaultSearch.filters,
      category_id:
        (defaultSearch.filters && defaultSearch.filters.category_id) || 9,
    },
  };

  const [search, setSearch] = useState(initialSearch);
  const [filter, setFilter] = useState(showModal ? "brand_id" : null);
  const [filterOptions, setFilterOptions] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setFilterOptions(null);
    setError(null);

    if (!filter) {
      return;
    }

    const loadValues = async () => {
      try {
        setLoading(true);
        setError(null);

        const preparedSearch = ProductSearchHelper.prepareForServer(search);
        const serverParams = {
          ...preparedSearch,
          filter,
        };

        const response = await axios.post(
          "/api/search/filter-values",
          serverParams
        );
        const data = response.data;

        if (filter === "price") {
          if (!data || !Array.isArray(data) || data.length === 0) {
            console.warn("No price data received, using defaults");
            setFilterOptions([{ min: 0, max: 1000000 }]);
            return;
          }

          const priceData = data[0];
          if (!priceData || typeof priceData !== "object") {
            console.warn("Invalid price data format:", priceData);
            setFilterOptions([{ min: 0, max: 1000000 }]);
            return;
          }

          const min = typeof priceData.min === "number" ? priceData.min : 0;
          const max =
            typeof priceData.max === "number" ? priceData.max : 1000000;

          if (min >= max) {
            console.warn("Invalid price range: min >= max", { min, max });
            setFilterOptions([{ min: 0, max: 1000000 }]);
            return;
          }

          setFilterOptions([{ min, max }]);
          return;
        }

        if (!Array.isArray(data)) {
          console.warn(`Invalid data format for filter ${filter}:`, data);
          setFilterOptions([]);
          return;
        }

        setFilterOptions(data);
      } catch (err) {
        console.error("Filter options loading error:", err);
        const errorMessage =
          err.response?.data?.error || "Failed to load filter options";
        setError(errorMessage);

        if (filter === "price") {
          console.log("Setting fallback price range due to error");
          setFilterOptions([{ min: 0, max: 1000000 }]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadValues();
  }, [filter, search.filters?.category_id, search.reset]);

  const onCancelClick = (e) => {
    e.preventDefault();
    close();
  };

  const onApplyClick = () => {
    onApply(search);
  };

  const onBackClick = () => {
    setFilter(null);
  };

  const categories = [
    { id: 9, name: "Watches" },
    { id: 3, name: "Bags" },
    { id: 4, name: "Cigars" },
  ];

  const onFilterChange = (filter) => {
    setFilterOptions(null);
    setFilter(filter);
  };

  const onFilterSelectionChange = (values) => {
    try {
      if (!filter) {
        console.warn("No filter selected for value change");
        return;
      }

      setSearch((prevSearch) => ({
        ...prevSearch,
        filters: {
          ...prevSearch.filters,
          [filter]: values || [],
        },
      }));
    } catch (err) {
      console.error("Filter selection change error:", err);
      setError("Failed to update filter selection");
    }
  };

  const onResetClick = () => {
    setSearch((prevSearch) => {
      const newSearch = {
        ...prevSearch,
        query: "",
        filters: {
          category_id: prevSearch.filters.category_id,
        },
        reset: (prevSearch.reset || 0) + 1,
      };
      return newSearch;
    });
  };

  const onCategoryClick = (categoryId) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      filters: { category_id: categoryId },
    }));
    setFilter("brand_id");
  };

  const onBulletRemoved = ({ filter, id }) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      filters: {
        ...prevSearch.filters,
        [filter]:
          (prevSearch.filters[filter] &&
            prevSearch.filters[filter].filter((o) => o.id !== id)) ||
          [],
      },
    }));
  };

  const onSortChange = (field, direction) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      sort: { field, direction },
    }));
  };

  const ParentComponent = showModal ? Modal : SlidingPane;

  const parentComponentProps = showModal
    ? {
        isOpen: active,
        onRequestClose: close,
        style: modalStyles,
      }
    : {
        className: "search-sliding-pane",
        isOpen: active,
        onRequestClose: close,
        hideHeader: true,
        overlayClassName: "search-overlay",
        shouldCloseOnEsc: false,
        from: "left",
      };

  const bulletFilters = {};
  let hasSelections = false;

  for (let filter in search.filters) {
    if (filter === "category_id") {
      continue;
    }

    bulletFilters[filter] = search.filters[filter];
    hasSelections = true;
  }

  return (
    <ParentComponent {...parentComponentProps}>
      <div
        id="filters"
        className="d-flex d-xl-flex flex-column flex-grow-1"
        style={
          showModal ? { height: "100%", minHeight: 0, maxHeight: "90vh" } : {}
        }
      >
        {showModal && (
          <div className="categories d-none d-xl-flex justify-content-start flex-shrink-0">
            <Tabs
              aria-label="Category"
              variant="underline"
              items={categories
                .filter((c) => c.id === search.filters.category_id)
                .map((c) => ({ value: c.id, label: c.name }))}
              value={search.filters.category_id}
              onChange={(categoryId) => onCategoryClick(categoryId)}
            />
          </div>
        )}

        <div
          className="d-xl-flex"
          style={{
            backgroundColor: "#FFF",
            ...(showModal
              ? {
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  maxHeight: "calc(90vh - 120px)",
                }
              : {}),
          }}
        >
          {(showModal || !filter) && (
            <FilterList
              active={filter}
              categoryId={search.filters.category_id}
              onChange={onFilterChange}
              includeStatusFilter={includeStatusFilter}
            />
          )}

          {filter && (
            <div className="filter-options-container">
              {!showModal && (
                <>
                  <Button
                    variant="ghost"
                    className="back"
                    data-btn="filters-open"
                    leadingIcon={<Icon name="ChevronLeft" size="sm" />}
                    onClick={onBackClick}
                  >
                    <SearchCount search={search} />
                  </Button>

                  <Separator noMargin className="mt-2 mb-0" />

                  {hasSelections && (
                    <>
                      <FilterBullets
                        filters={bulletFilters}
                        onRemove={onBulletRemoved}
                        className="inverted-colors mt-3 mx-3 mb-0"
                      />
                      <Separator noMargin className="mt-0 mb-0" />
                    </>
                  )}
                </>
              )}

              {error && (
                <div className="error-message p-3 text-center text-danger">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="d-flex justify-content-center p-3">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <FilterOptions
                  filter={filter}
                  options={filterOptions}
                  onChange={onFilterSelectionChange}
                  onSortChange={onSortChange}
                  sort={search.sort}
                  selections={search.filters[filter] || []}
                />
              )}

              {filterOptions && showModal && (
                <div className="count">
                  <SearchCount search={search} />
                </div>
              )}

              <div className="d-xl-none d-flex mt-3 mb-3 apply-filters-button">
                <Button
                  variant="primary"
                  fullWidth
                  data-btn="filters-confirm-sheet"
                  onClick={onApplyClick}
                >
                  Apply filter
                </Button>
              </div>
            </div>
          )}
        </div>

        {showModal && (
          <div className="actions d-none d-xl-flex justify-content-between flex-shrink-0">
            <Button variant="ghost" data-btn="filters-cancel" onClick={onCancelClick}>
              Cancel
            </Button>

            <ButtonGroup align="end">
              <Button variant="secondary" data-btn="filters-reset" onClick={onResetClick}>
                Reset
              </Button>
              <Button variant="primary" data-btn="filters-confirm" onClick={onApplyClick}>
                Apply
              </Button>
            </ButtonGroup>
          </div>
        )}
      </div>
    </ParentComponent>
  );
};

export default SearchFilters;
